import { CookiesProvider, useCookies } from 'react-cookie';

import AnalyticsProvider from '@blueshift-ui/analytics/dist/providers/analytics-provider';
import { BrowserRouter } from 'react-router-dom';
import { FetchContextProvider } from '@llt-frontend/react-fetch';
import { LoadingContextProvider } from './loading-context';
import PropTypes from 'prop-types';
import React from 'react';
import { SnackbarContextProvider } from '@llt-frontend/react-material-snackbar';
import { TrackInteractionProvider } from '@blueshift-ui/telemetry';
import getBasePathAttributes from '../utilities/routing/get-base-path-attributes';
import newRelicAgent from '../utilities/new-relic';
import useLabUser from '../hooks/data-access/use-lab-user';
import { v4 as uuid } from 'uuid';

function _TrackInteractionProvider({ children }) {
  const inPreviewMode = process.env.REACT_APP_MODE !== 'production';
  const labUser = useLabUser();
  const [cookies, setCookie] = useCookies();

  const labUserId = labUser.activeUser?.id;
  const labUserRole = labUser.activeUser?.primaryRole || labUser.activeUser?.roles?.[0];
  const userName = labUser.activeUser?.firstName + ' ' + labUser.activeUser?.lastName;
  const trackedEntity = React.useMemo(
    function _cacheTrackedEntity() {
      if (!labUserId) {
        return;
      }

      return {
        id: labUserId,
        type: labUserRole,
      };
    },
    [labUserId, labUserRole]
  );
  const visitorId = React.useRef(cookies.visitor_id || uuid());

  //
  // Side-Effects

  React.useEffect(
    function afterVisitorId() {
      if (!cookies.visitor_id) {
        setCookie('visitor_id', visitorId.current);
      }
    },
    [cookies.visitor_id, setCookie]
  );

  if (trackedEntity) {
    newRelicAgent.setCustomAttribute('userId', trackedEntity.id);
    newRelicAgent.setCustomAttribute('userRole', trackedEntity.type);
  }
  if (userName) {
    newRelicAgent.setCustomAttribute('userName', userName);
  }

  //
  // Render
  return (
    <TrackInteractionProvider
      applicationId="llt-lab-frontend"
      disabled={process.env.NODE_ENV === 'development'}
      entity={trackedEntity}
      preview={inPreviewMode}
      segmentDisabled={!process.env.REACT_APP_SEGMENT_INTERACTION_API_KEY}
      segmentWriteKey={process.env.REACT_APP_SEGMENT_INTERACTION_API_KEY}
      userId={labUserId}
      visitorId={visitorId.current}
    >
      {children}
    </TrackInteractionProvider>
  );
}

_TrackInteractionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function AppContext({ children }) {
  const { basePath } = getBasePathAttributes();

  return (
    <CookiesProvider>
      <FetchContextProvider>
        <BrowserRouter basename={basePath}>
          <LoadingContextProvider>
            <AnalyticsProvider
              options={{
                sessionReplayApiKey: `${process.env.REACT_APP_AMPLITUDE_API_KEY ?? ''}`,
              }}
              writeKey={process.env.REACT_APP_SEGMENT_INTERACTION_API_KEY}
            >
              <_TrackInteractionProvider>
                <SnackbarContextProvider>{children}</SnackbarContextProvider>
              </_TrackInteractionProvider>
            </AnalyticsProvider>
          </LoadingContextProvider>
        </BrowserRouter>
      </FetchContextProvider>
    </CookiesProvider>
  );
}

AppContext.propTypes = {
  children: PropTypes.node.isRequired,
};

// Exports
// -------

export default AppContext;
